/* eslint-disable no-await-in-loop */
import defaultHeaders from './default-headers';

/**
 * Fetch POST request utility
 * @param {string} url - The URL to send the POST request to.
 * @param {any} data - The data to be sent in the request body.
 * @param {boolean} isFileUpload - Whether the request is for a file upload.
 * @param {any} onProgress - Optional callback for progress updates.
 * @returns {any} - The response from the server.
 */
const patchStream = async (
  url: string,
  data: any,
  isFileUpload = false,
  onProgress?: (progress: any) => void,
): Promise<Response> => {
  try {
    const customHeaders: HeadersInit = isFileUpload
      ? {} // FormData automatically sets the headers
      : { 'Content-Type': 'application/json' };

    const authToken = defaultHeaders();
    const headers: HeadersInit = {
      ...(authToken && authToken),
      ...customHeaders,
    };

    const requestBody = isFileUpload ? data : JSON.stringify(data);

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: requestBody,
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      let errorData = null;

      try {
        const errorText = await response.text();
        try {
          errorData = JSON.parse(errorText);
          errorMessage = errorData?.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
      } catch (error) {
        console.error('Failed to read error response:', error);
      }

      if (response.status === 413) {
        errorMessage = 'File size is too large. Please upload smaller images.';
      }

      const customError = new Error(errorMessage);
      (customError as any).response = {
        status: response.status,
        data: {
          message: errorMessage,
        },
      };
      throw customError;
    }

    // Handle response stream if onProgress callback is provided
    if (onProgress) {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffer = '';

      while (!done && reader) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        if (done && !value) {
          break;
        }

        const chunk = decoder.decode(value || new Uint8Array(), { stream: true });
        buffer += chunk;

        let processable = '';
        if (done) {
          processable = buffer;
          buffer = '';
        } else {
          const boundary = buffer.lastIndexOf('}');
          if (boundary !== -1) {
            processable = buffer.slice(0, boundary + 1);
            buffer = buffer.slice(boundary + 1);
          }
        }

        if (processable) {
          const updates = processable
            .split(/(?<=\})\s*(?=\{)/)
            .map((p) => p.trim())
            .filter(Boolean);

          updates.forEach((update) => {
            try {
              const parsedUpdate = JSON.parse(update);

              // Check for backend-sent error and handle it
              if (parsedUpdate.error) {
                // Construct custom error object
                const customError = new Error(parsedUpdate.message);
                (customError as any).response = {
                  data: {
                    message: parsedUpdate.message,
                  },
                };
                throw customError;
              }

              onProgress(parsedUpdate); // Call the progress update callback
            } catch (error) {
              console.error('Error parsing JSON update:', error, 'Raw update:', update);
              throw error; // Re-throw the error to exit the loop
            }
          });
        }
      }
    }

    return response;
  } catch (error) {
    console.error('Error in postRequest:', error);
    throw error;
  }
};

export default patchStream;
